import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/**
 * PUBLIC (no-login) exam-result view — "phiếu khám" opened from a tokenized,
 * QR-linked URL like {@code /view-order/<token>/<orderCode>.html} (the
 * {@code .html} suffix mirrors legacy print links and is stripped here).
 *
 * <p>The page MUST work with no session: it calls the public backend endpoint
 * with a BARE {@code fetch} (never the shared apiClient — that injects a
 * bearer token + tenant header, which the public endpoint refuses and a
 * logged-out visitor doesn't have). Same origin; nginx proxies {@code /api}.
 *
 * <p>Excluded from search-engine indexing two ways: {@code public/robots.txt}
 * disallows {@code /view-order/}, the backend returns {@code X-Robots-Tag:
 * noindex}, and we also inject a {@code <meta name="robots">} client-side.
 */

type PublicGender = "M" | "F" | "OTHER" | "UNKNOWN" | string | null;

interface PublicVitals {
  systolicBp: number | null;
  diastolicBp: number | null;
  heartRate: number | null;
  temperatureC: number | null;
  respiratoryRate: number | null;
  spO2: number | null;
  weightKg: number | null;
  heightCm: number | null;
  bmi: number | null;
  recordedAt: string | null;
}

interface PublicDiagnosis {
  id: string;
  icd10Code: string | null;
  label: string;
  primary: boolean;
  note: string | null;
}

interface PublicPrescriptionItem {
  id: string;
  medicineName: string;
  medicineStrength: string | null;
  medicineUnit: string | null;
  quantity: number | null;
  doseMorning: number | null;
  doseNoon: number | null;
  doseAfternoon: number | null;
  doseEvening: number | null;
  doseUnit: string | null;
  route: string | null;
  mealTiming: string | null;
  note: string | null;
}

interface PublicPrescription {
  signed: boolean;
  signedAt: string | null;
  items: PublicPrescriptionItem[];
  pdfUrl: string | null;
}

interface InactiveLink {
  code: string;
  state: "EXPIRED" | "REVOKED" | string;
  clinicName: string | null;
  clinicPhone: string | null;
}

interface PublicEncounter {
  clinicName: string | null;
  clinicAddress: string | null;
  clinicPhone: string | null;
  orderCode: string | null;
  patientName: string | null;
  patientCode: string | null;
  patientGender: PublicGender;
  patientAge: number | null;
  examDate: string | null;
  doctorName: string | null;
  status: "SIGNED" | "IN_PROGRESS" | "CANCELLED" | string;
  chiefComplaint: string | null;
  vitals: PublicVitals | null;
  diagnoses: PublicDiagnosis[];
  prescription: PublicPrescription | null;
  advice: string | null;
}

const PUBLIC_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

/** Strip a trailing ".html" (case-insensitive) from the order-code param. */
function stripHtml(code: string | undefined): string {
  if (!code) return "";
  return code.replace(/\.html?$/i, "");
}

export function PublicEncounterViewPage() {
  const params = useParams();
  const token = params.token ?? "";
  // Route uses a splat (`*`) so a param carrying ".html" always matches; the
  // human-facing order code is whatever segment came after the token.
  const rawCode = params.code ?? params["*"] ?? "";
  const orderCode = stripHtml(rawCode);

  const [data, setData] = useState<PublicEncounter | null>(null);
  const [state, setState] = useState<
    "loading" | "ok" | "notfound" | "inactive" | "error"
  >("loading");
  // #19150 — the link exists but has expired or been revoked: 410 with the
  // clinic's name/phone (never anything about the visit).
  const [inactive, setInactive] = useState<InactiveLink | null>(null);

  // Belt-and-braces noindex on the client (backend already sends X-Robots-Tag).
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setState("notfound");
      return;
    }
    let alive = true;
    setState("loading");
    fetch(`${PUBLIC_BASE}/v1/public/encounters/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (r.status === 410) {
          const body = (await r.json().catch(() => null)) as InactiveLink | null;
          if (alive) setInactive(body);
          return Promise.reject(410);
        }
        return r.ok ? r.json() : Promise.reject(r.status);
      })
      .then((json: PublicEncounter) => {
        if (!alive) return;
        setData(json);
        setState("ok");
      })
      .catch((status) => {
        if (!alive) return;
        setState(
          status === 404 ? "notfound" : status === 410 ? "inactive" : "error"
        );
      });
    return () => {
      alive = false;
    };
  }, [token]);

  if (state === "loading") {
    return (
      <div className={SHELL}>
        <PublicStyles />
        <div className={`${CARD} text-center text-muted-foreground`}>
          Đang tải phiếu khám…
        </div>
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className={SHELL}>
        <PublicStyles />
        <div className={`${CARD} text-center`}>
          <h2 className="mb-2 text-lg font-semibold text-foreground">
            Không tìm thấy phiếu khám
          </h2>
          <p className="text-sm text-muted-foreground">
            Đường liên kết không hợp lệ hoặc đã hết hiệu lực
            {orderCode ? ` (mã ${orderCode})` : ""}. Vui lòng liên hệ phòng khám
            để được hỗ trợ.
          </p>
        </div>
      </div>
    );
  }

  if (state === "inactive") {
    const clinic = inactive?.clinicName?.trim() || "phòng khám";
    const phone = inactive?.clinicPhone?.trim();
    return (
      <div className={SHELL}>
        <PublicStyles />
        <div className={`${CARD} text-center`}>
          <h2 className="mb-2 text-lg font-semibold text-foreground">
            {inactive?.state === "REVOKED"
              ? "Liên kết xem phiếu đã bị thu hồi"
              : "Liên kết xem phiếu đã hết hạn"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {inactive?.state === "REVOKED"
              ? "Phòng khám đã thu hồi liên kết này"
              : "Để bảo vệ thông tin sức khỏe của bạn, liên kết xem phiếu khám chỉ dùng được trong 7 ngày"}
            {orderCode ? ` (mã ${orderCode})` : ""}. Vui lòng liên hệ {clinic}
            {phone ? (
              <>
                {" "}qua số{" "}
                <a className="font-medium text-primary" href={`tel:${phone.replace(/\s/g, "")}`}>
                  {phone}
                </a>
              </>
            ) : null}{" "}
            để được cấp lại.
          </p>
        </div>
      </div>
    );
  }

  if (state === "error" || !data) {
    return (
      <div className={SHELL}>
        <PublicStyles />
        <div className={`${CARD} text-center`}>
          <h2 className="mb-2 text-lg font-semibold text-foreground">
            Đã xảy ra lỗi
          </h2>
          <p className="text-sm text-muted-foreground">
            Không thể tải phiếu khám lúc này. Vui lòng thử lại sau.
          </p>
        </div>
      </div>
    );
  }

  const e = data;
  const v = e.vitals;
  const primary = e.diagnoses.find((d) => d.primary) ?? e.diagnoses[0];
  const others = e.diagnoses.filter((d) => d !== primary);
  const rxItems = e.prescription?.items ?? [];

  return (
    <div className={SHELL}>
      <PublicStyles />

      <div className="mb-3 print:hidden">
        <Button type="button" onClick={() => window.print()}>
          <Printer aria-hidden="true" />
          In phiếu
        </Button>
      </div>

      <article className={CARD}>
        <header className="border-b-2 border-black pb-2 text-center">
          <h1 className="text-xl font-bold text-primary">
            {e.clinicName ?? "Phòng khám"}
          </h1>
          {e.clinicAddress && (
            <div className="mt-0.5 text-xs text-muted-foreground">
              {e.clinicAddress}
            </div>
          )}
          {e.clinicPhone && (
            <div className="mt-0.5 text-xs text-muted-foreground">
              ĐT: {e.clinicPhone}
            </div>
          )}
        </header>

        <h2 className="mt-3 mb-0.5 text-center text-[17px] font-semibold tracking-wide">
          PHIẾU KHÁM BỆNH
        </h2>
        {e.orderCode && (
          <div className="text-center text-xs text-muted-foreground">
            Mã phiếu: {e.orderCode}
          </div>
        )}
        {e.status === "CANCELLED" && (
          <div className="mt-2 flex justify-center">
            <Badge variant="destructive">PHIẾU ĐÃ HỦY</Badge>
          </div>
        )}

        <section className="my-2 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
          <div>
            <strong>Bệnh nhân:</strong> {e.patientName ?? "—"}
          </div>
          <div>
            <strong>Mã BN:</strong> {e.patientCode ?? "—"}
          </div>
          <div>
            <strong>Giới tính:</strong> {genderLabel(e.patientGender)}
          </div>
          <div>
            <strong>Tuổi:</strong> {e.patientAge ?? "—"}
          </div>
          <div>
            <strong>Ngày khám:</strong> {formatDate(e.examDate)}
          </div>
          <div>
            <strong>Bác sĩ:</strong> {e.doctorName ?? "—"}
          </div>
        </section>

        {e.chiefComplaint && (
          <section className="mt-3.5">
            <h3 className={BLOCK_HEADING}>Lý do khám</h3>
            <p className="whitespace-pre-line">{e.chiefComplaint}</p>
          </section>
        )}

        {v && hasAnyVital(v) && (
          <section className="mt-3.5">
            <h3 className={BLOCK_HEADING}>Dấu hiệu sinh tồn</h3>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {v.systolicBp != null && v.diastolicBp != null && (
                <span>
                  HA: {v.systolicBp}/{v.diastolicBp} mmHg
                </span>
              )}
              {v.heartRate != null && <span>Mạch: {v.heartRate} lần/ph</span>}
              {v.temperatureC != null && <span>Nhiệt độ: {v.temperatureC}°C</span>}
              {v.spO2 != null && <span>SpO₂: {v.spO2}%</span>}
              {v.weightKg != null && <span>Cân nặng: {v.weightKg} kg</span>}
            </div>
          </section>
        )}

        {e.diagnoses.length > 0 && (
          <section className="mt-3.5">
            <h3 className={BLOCK_HEADING}>Chẩn đoán</h3>
            <ul className="list-disc pl-[18px]">
              {primary && (
                <li className="mb-0.5">
                  {primary.icd10Code ? `[${primary.icd10Code}] ` : ""}
                  {primary.label}{" "}
                  <Badge className="ml-1 px-1.5 py-0 text-[10px]">Chính</Badge>
                  {primary.note ? ` — ${primary.note}` : ""}
                </li>
              )}
              {others.map((d) => (
                <li key={d.id} className="mb-0.5">
                  {d.icd10Code ? `[${d.icd10Code}] ` : ""}
                  {d.label}
                  {d.note ? ` — ${d.note}` : ""}
                </li>
              ))}
            </ul>
          </section>
        )}

        {rxItems.length > 0 && (
          <section className="mt-3.5">
            <h3 className={BLOCK_HEADING}>Đơn thuốc</h3>
            <table className="mt-1 w-full border-collapse [font-variant-numeric:tabular-nums]">
              <thead>
                <tr>
                  <th className={`${TH_TD} text-left`}>Thuốc</th>
                  <th className={`${TH_TD} w-[70px] text-left`}>SL</th>
                  <th className={`${TH_TD} w-[150px] text-left`}>Cách dùng</th>
                </tr>
              </thead>
              <tbody>
                {rxItems.map((it) => (
                  <tr key={it.id}>
                    <td className={TH_TD}>
                      {it.medicineName}
                      {it.medicineStrength ? ` ${it.medicineStrength}` : ""}
                    </td>
                    <td className={TH_TD}>
                      {it.quantity ?? "—"}
                      {it.medicineUnit ? ` ${it.medicineUnit}` : ""}
                    </td>
                    <td className={TH_TD}>{doseSummary(it)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {e.advice && (
          <section className="mt-3.5">
            <h3 className={BLOCK_HEADING}>Lời dặn</h3>
            {/* One advice per line, as the doctor entered them (#19171, L7). */}
            <p className="whitespace-pre-line" data-testid="public-advice">{e.advice}</p>
          </section>
        )}

        <footer className="mt-5 flex items-start justify-between">
          <div>{formatDate(e.examDate)}</div>
          <div>
            <strong>Bác sĩ điều trị</strong>
            <div className="mt-10 text-center">{e.doctorName ?? ""}</div>
          </div>
        </footer>
      </article>
    </div>
  );
}

const SHELL =
  "min-h-screen bg-muted px-4 py-6 font-sans print:bg-white print:p-0";

const CARD =
  "mx-auto max-w-[148mm] bg-white p-[12mm] text-[13px] leading-relaxed text-foreground shadow-md [-webkit-print-color-adjust:exact] [print-color-adjust:exact] print:m-0 print:shadow-none";

const BLOCK_HEADING =
  "mb-1 border-b border-border pb-0.5 text-[13px] font-semibold";

const TH_TD =
  "border border-muted-foreground/50 px-1.5 py-1 align-top text-xs";

/**
 * Print-only CSS that Tailwind utility classes cannot express: the A5 page
 * box (`@page`) and forcing background/foreground colours to render in print.
 * All visual layout otherwise lives in Tailwind utilities on the markup.
 */
function PublicStyles() {
  return (
    <style>{`
      @page { size: A5; margin: 10mm; }
    `}</style>
  );
}

function genderLabel(g: PublicGender): string {
  switch (g) {
    case "M":
      return "Nam";
    case "F":
      return "Nữ";
    case "OTHER":
      return "Khác";
    default:
      return "—";
  }
}

function hasAnyVital(v: PublicVitals): boolean {
  return (
    v.systolicBp != null ||
    v.diastolicBp != null ||
    v.heartRate != null ||
    v.temperatureC != null ||
    v.spO2 != null ||
    v.weightKg != null ||
    v.heightCm != null ||
    v.respiratoryRate != null
  );
}

function doseSummary(it: PublicPrescriptionItem): string {
  const parts: string[] = [];
  if (it.doseMorning) parts.push(`Sáng ${it.doseMorning}`);
  if (it.doseNoon) parts.push(`Trưa ${it.doseNoon}`);
  if (it.doseAfternoon) parts.push(`Chiều ${it.doseAfternoon}`);
  if (it.doseEvening) parts.push(`Tối ${it.doseEvening}`);
  let s = parts.length === 0 ? "Theo chỉ dẫn" : parts.join(" · ");
  if (it.doseUnit) s += ` ${it.doseUnit}`;
  const meal = mealTimingLabel(it.mealTiming);
  if (meal) s += ` · ${meal}`;
  if (it.note) s += ` · ${it.note}`;
  return s;
}

function mealTimingLabel(m: string | null): string {
  switch (m) {
    case "BEFORE_MEAL":
      return "Trước ăn";
    case "WITH_MEAL":
      return "Trong bữa ăn";
    case "AFTER_MEAL":
      return "Sau ăn";
    case "EMPTY_STOMACH":
      return "Lúc đói";
    case "BEDTIME":
      return "Trước khi ngủ";
    default:
      return "";
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
